import { RoomConfiguration } from '@components/RoomConfigurator';
import type { Room } from '@interfaces/expedia/property';
import { RoomWithRate } from './RoomsList';

export function sortRoomsByOrder(rooms: Room[], selectedSortingOption: string) {
  const order = selectedSortingOption === 'price-low-high' ? 1 : -1;

  // Flatten the room and rate structure into an array of { room, rate } pairs
  const rateList = rooms.reduce((acc: RoomWithRate[], room) => {
    const ratesWithRoom = room.rates
      // .filter((val) => val.occupancyPricing?.length)
      .map((rate) => {
        return {
          room,
          rate,
          price: parseFloat(rate.occupancyPricing[0]?.total.value), // Assumes the first occupancy pricing is the relevant one
        };
      });
    return acc.concat(ratesWithRoom);
  }, []);

  // Sort the rate list by price
  rateList.sort((a, b) => order * (a.price - b.price));

  return rateList;
}

export function sortRoomsByRoomType(rooms: Room[], sortingOption: string) {
  if (!sortingOption) return rooms;
  // Split the sortingOption string into an array of filter options
  const filterOptions = sortingOption.split(',');

  const filteredListRoom =
    rooms?.filter((r) =>
      filterOptions.some((roomFeature) => {
        if (roomFeature.includes('Smoking')) {
          return r.amenities.includes(roomFeature);
        }

        return (
          r.rates?.some((rate) => {
            return rate.bedgroups?.some((bedgroup) => {
              return bedgroup.description
                .toLowerCase()
                .replaceAll(' ', '')
                .replaceAll('-', '')
                .includes(roomFeature.trim()); // Trim roomFeature to remove whitespace
            });
          }) ||
          r.name
            .toLowerCase()
            .replaceAll(' ', '')
            .replaceAll('-', '')
            .includes(roomFeature.trim()) // Trim roomFeature to remove whitespace
        );
      })
    ) ?? null;

  return filteredListRoom;
}

export function buildOccupancies(roomConfigurations: RoomConfiguration[]) {
  const occupancies: string[] = [];

  roomConfigurations.forEach((roomConfiguration) => {
    let occupancy = `${roomConfiguration.adults}`;

    if (roomConfiguration.children.length) {
      occupancy += '-';

      roomConfiguration.children.forEach((age) => {
        occupancy += age;
        occupancy += ',';
      });

      occupancy = occupancy.substr(0, occupancy.length - 1);
    }
    occupancies.push(occupancy);
  });

  return occupancies;
}

export function remainingOccupancies(
  roomConfigurations: RoomConfiguration[],
  roomSelections: { occupancy: string }[]
) {
  const remaining = buildOccupancies(roomConfigurations);
  for (const selection of roomSelections) {
    const index = remaining.indexOf(selection.occupancy);
    if (index > -1) remaining.splice(index, 1);
  }
  return remaining;
}

export function roomStateForHotel({
  hotelId,
  previousHotelId,
  roomConfigurations,
  roomSelections,
}: {
  hotelId: number;
  previousHotelId: number | null | undefined;
  roomConfigurations: RoomConfiguration[];
  roomSelections: { occupancy: string }[];
}) {
  const switched = previousHotelId != null && previousHotelId !== hotelId;
  const nextSelections = switched ? [] : roomSelections;
  return {
    selectedHotelId: hotelId,
    roomSelections: nextSelections,
    selectableOccupancies: remainingOccupancies(
      roomConfigurations,
      nextSelections
    ),
  };
}

// Admin hotel replacement: the failed hold's rooms are stored (scoped) as the "selected hotel";
// on that same hotel's page, start from those rooms instead of an empty selection so the admin
// only changes what needs changing. Anything else (another hotel, no match in the stored
// rooms, a selection that doesn't fit the room slots) starts blank as usual.
export function replacementRoomPrefill<T extends { occupancy: string }>({
  isHotelReplace,
  hotelId,
  storedHotel,
  roomConfigurations,
  roomSelections,
}: {
  isHotelReplace: boolean;
  hotelId: number | string;
  storedHotel: { id?: unknown; roomSelections?: T[] } | null | undefined;
  roomConfigurations: RoomConfiguration[];
  roomSelections: T[];
}): T[] | null {
  const stored = storedHotel?.roomSelections;
  if (
    !isHotelReplace ||
    roomSelections.length > 0 ||
    // the page hands the id over as a string at runtime
    Number(storedHotel?.id) !== Number(hotelId) ||
    !Array.isArray(stored) ||
    stored.length === 0 ||
    stored.length > roomConfigurations.length
  ) {
    return null;
  }
  return stored;
}
